import { MarketingDrawer } from '@app/components/general'
import { metaSetter } from '@app/utils'
import type { PageProps } from '@app/types'
import { OSSRoutes } from '@app/configs'
import { URL } from 'url'
import { GrStar } from 'react-icons/gr'
import { SectionContainer } from '@app/components/stateless/containers/section-container'
import { Header, Header3 } from '@app/components/general/header'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import loadNamespaces from 'next-translate/loadNamespaces'
import type { GetStaticProps } from 'next'

function OSSCell({ item }: { item: (typeof OSSRoutes)[number] }) {
  return (
    <a
      href={item.href}
      rel='noreferrer'
      target='_blank'
      className='border-2 rounded block py-4 px-4 w-full h-full hover:text-blue-600 hover:border-blue-600 dark:hover:border-blue-600'
    >
      <div>
        <Header3 className='text-xl font-bold'>{item.name}</Header3>
        <p className='text-base md:text-lg'>{item.description}</p>
        <div className='flex place-content-between place-items-center pt-3'>
          <div className='text-sm'>
            {item.fullName ? item.fullName.toLowerCase() : 'N/A'}
          </div>
          <div className='flex space-x-2 text-xs place-items-center '>
            {item.stars ?? 0}
            <GrStar className='grIcon w-3 h-3' />
          </div>
        </div>
      </div>
    </a>
  )
}

function OpenSource({
  items,
}: PageProps & {
  items: typeof OSSRoutes
}) {
  const { t } = useTranslation('oss')

  return (
    <MarketingDrawer>
      <SectionContainer container block>
        <Header>{t('header')}</Header>
        <h2 className='py-4 text-blue-600 text-sm'>{t('ethos')}</h2>
        <p className='text-base pt-1 pb-4 text-gray-800 dark:text-gray-200'>
          {t('header-details')}
        </p>
        <ul className='py-2 gap-x-2 gap-y-2 grid grid-cols-1 sm:grid-cols-2'>
          {items?.map((item) => (
            <li key={item.name}>
              <OSSCell item={item} />
            </li>
          ))}
        </ul>
      </SectionContainer>
    </MarketingDrawer>
  )
}

export const getStaticProps: GetStaticProps = async (ctx: any) => {
  const getItems = async (): Promise<typeof OSSRoutes> => {
    const items: typeof OSSRoutes = []

    for await (const oss of OSSRoutes) {
      let path

      try {
        path = new URL(oss.href)?.pathname
      } catch (e) {
        console.error(e)
      }

      let props
      let res
      try {
        res = await fetch(`http://api.github.com/repos${path}`, {
          headers: {
            Authorization: process.env.GITHUB_TOKEN || '',
          },
        })
      } catch (e) {
        console.error(e)
      }

      if (res && res?.ok) {
        try {
          props = await res?.json()
        } catch (e) {
          console.error(e)
        }
      }

      if (props) {
        items.push({
          name: props?.name,
          description: props?.description,
          href: props?.html_url,
          stars: props?.stargazers_count,
          fullName: props?.full_name,
        })
      }
    }

    return items
  }

  const items = await getItems()

  const namespaces = await loadNamespaces({
    ...ctx,
    pathname: '/open-source',
  })

  return {
    props: {
      ...namespaces,
      items: items?.length ? items : OSSRoutes,
    },
    revalidate: 3600 * 12, // every 12 hours
  }
}

export default metaSetter(
  { OpenSource },
  {
    translation: 'oss',
  }
)
